import React from 'react';

import {
  formatLeavingGroup,
  parseLeavingGroupTypedValue,
} from 'molbio/adcs/linker-payload-connection/AdcLinkerPayloadLeavingGroupSerialization';
import useLinkerPayloadLeavingGroups from 'molbio/adcs/registration-table/useLinkerPayloadLeavingGroups';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

interface LeavingGroupWithSmilesProps {
  leavingGroup: number;
}

/** Shows the leaving group with the SMILES the row's linker-payload has for it, if any. */
function LeavingGroupWithSmiles({leavingGroup}: LeavingGroupWithSmilesProps): JSX.Element {
  const leavingGroupsState = useLinkerPayloadLeavingGroups();
  const smiles =
    leavingGroupsState.status === 'loaded' && 'leavingGroups' in leavingGroupsState.result
      ? (leavingGroupsState.result.leavingGroups.find(({number}) => number === leavingGroup)?.smiles ?? null)
      : null;
  return <>{formatLeavingGroup(leavingGroup, smiles)}</>;
}

interface AdcLinkerPayloadLeavingGroupDisplayProps {
  typedValue: TypedValue;
}

/**
 * Read-only Linker-payload leaving group cell in an ADC registration table. Shows the leaving group with its
 * SMILES when the row's linker-payload has it (see getLinkerPayloadLeavingGroupsResult), e.g.
 * "LG2 (ON1C(=O)CCC1=O)", and just the leaving group otherwise, including while the SMILES load. The SMILES are
 * only looked up for a cell holding a leaving group. Must render inside a table row (see RowModelProvider).
 */
export default function AdcLinkerPayloadLeavingGroupDisplay({
  typedValue,
}: AdcLinkerPayloadLeavingGroupDisplayProps): JSX.Element | null {
  const parsed = parseLeavingGroupTypedValue(typedValue);
  if ('error' in parsed || parsed.leavingGroup == null) {
    // Shows text that isn't a leaving group label as it is; the column's custom eval flags it.
    return typedValue.type === BaseTypedValueType.STRING ? <>{typedValue.rawValue}</> : null;
  }
  return <LeavingGroupWithSmiles leavingGroup={parsed.leavingGroup} />;
}
